import { Admin, Resource, defaultDarkTheme, Layout, Menu } from "react-admin";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, Typography } from "@mui/material";
import { Image, FolderOpen, Mail, ArrowLeft, FileText } from "lucide-react";

import { dataProvider } from "./dataProvider";
import { authProvider } from "./authProvider";
import { MediaList } from "./MediaList";
import { MediaCreate } from "./MediaCreate";
import { MediaEdit } from "./MediaEdit";
import { SystemLogsList } from "./SystemLogsList";

// Custom dark theme matching the HHS Build aesthetic
const hhsTheme = createTheme({
  ...defaultDarkTheme,
  palette: {
    mode: "dark",
    primary: {
      main: "#22d3ee", // Cyan
      light: "#67e8f9",
      dark: "#0891b2",
    },
    secondary: {
      main: "#a855f7", // Purple
      light: "#c084fc",
      dark: "#7c3aed",
    },
    background: {
      default: "#0f0f1a",
      paper: "#1a1a2e",
    },
    text: {
      primary: "#e2e8f0",
      secondary: "#94a3b8",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#1a1a2e",
          borderBottom: "1px solid rgba(34, 211, 238, 0.2)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#0f0f1a",
          borderRight: "1px solid rgba(34, 211, 238, 0.2)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          background: "linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)",
          "&:hover": {
            background: "linear-gradient(135deg, #67e8f9 0%, #22d3ee 100%)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#1a1a2e",
          border: "1px solid rgba(34, 211, 238, 0.1)",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid rgba(34, 211, 238, 0.1)",
        },
      },
    },
  },
});

// Custom menu with navigation links
// Note: Links within react-admin use relative paths (e.g., "/media")
// External links use full paths and need to use regular anchor tags
const CustomMenu = () => (
  <Menu>
    <Menu.Item
      to="/media"
      primaryText="Media Library"
      leftIcon={<Image size={20} />}
    />
    <Menu.Item
      to="/system_logs"
      primaryText="System Logs"
      leftIcon={<FileText size={20} />}
    />
    <Box sx={{ borderTop: "1px solid rgba(34, 211, 238, 0.1)", my: 2 }} />
    {/* External links - use anchor tags to navigate outside react-admin */}
    <Box
      component="a"
      href="/admin"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1,
        color: "#94a3b8",
        textDecoration: "none",
        "&:hover": { color: "#22d3ee", backgroundColor: "rgba(34, 211, 238, 0.1)" },
      }}
    >
      <Mail size={20} />
      <span>Contact Requests</span>
    </Box>
    <Box
      component="a"
      href="/"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1,
        color: "#94a3b8",
        textDecoration: "none",
        "&:hover": { color: "#22d3ee", backgroundColor: "rgba(34, 211, 238, 0.1)" },
      }}
    >
      <ArrowLeft size={20} />
      <span>Back to Site</span>
    </Box>
  </Menu>
);

// Custom layout with the custom menu
const CustomLayout = (props: any) => (
  <Layout {...props} menu={CustomMenu} />
);

// Dashboard component
const Dashboard = () => (
  <Box
    sx={{
      p: 4,
      textAlign: "center",
      backgroundColor: "#1a1a2e",
      minHeight: "80vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <FolderOpen size={64} color="#22d3ee" style={{ marginBottom: 24 }} />
    <Typography variant="h4" sx={{ color: "#22d3ee", mb: 2 }}>
      HHS Build Media CMS
    </Typography>
    <Typography variant="body1" sx={{ color: "#94a3b8", mb: 4, maxWidth: 500 }}>
      Upload and manage images and videos for your website. All media is stored
      securely on Cloudinary with automatic optimization.
    </Typography>
    <Box sx={{ display: "flex", gap: 2 }}>
      <Box
        sx={{
          p: 3,
          backgroundColor: "rgba(34, 211, 238, 0.1)",
          borderRadius: 2,
          border: "1px solid rgba(34, 211, 238, 0.2)",
          textAlign: "center",
          minWidth: 150,
        }}
      >
        <Image size={32} color="#22d3ee" />
        <Typography variant="h6" sx={{ color: "#e2e8f0", mt: 1 }}>
          Images
        </Typography>
        <Typography variant="caption" sx={{ color: "#94a3b8" }}>
          JPG, PNG, WebP, GIF
        </Typography>
      </Box>
      <Box
        sx={{
          p: 3,
          backgroundColor: "rgba(168, 85, 247, 0.1)",
          borderRadius: 2,
          border: "1px solid rgba(168, 85, 247, 0.2)",
          textAlign: "center",
          minWidth: 150,
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#a855f7"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
          </svg>
        </Box>
        <Typography variant="h6" sx={{ color: "#e2e8f0", mt: 1 }}>
          Videos
        </Typography>
        <Typography variant="caption" sx={{ color: "#94a3b8" }}>
          MP4, WebM, MOV
        </Typography>
      </Box>
    </Box>
  </Box>
);

/**
 * MediaCMS - React-Admin based CMS for managing media
 * Integrates with Cloudinary for storage and our Express API for metadata
 * 
 * ROUTING: We wrap Admin with BrowserRouter using basename="/cms" to ensure
 * proper routing when the CMS is mounted at /cms. This prevents conflicts
 * with the main app's wouter router.
 */
export const MediaCMS = () => {
  return (
    <BrowserRouter basename="/cms">
      <ThemeProvider theme={hhsTheme}>
        <CssBaseline />
        <Admin
          dataProvider={dataProvider}
          authProvider={authProvider}
          layout={CustomLayout}
          dashboard={Dashboard}
          darkTheme={hhsTheme}
          defaultTheme="dark"
          title="HHS Build CMS"
        >
          <Resource
            name="media"
            list={MediaList}
            create={MediaCreate}
            edit={MediaEdit}
            icon={Image}
            options={{ label: "Media Library" }}
          />
          <Resource
            name="system_logs"
            list={SystemLogsList}
            icon={FileText}
            options={{ label: "System Logs" }}
          />
        </Admin>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default MediaCMS;

